import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { InfoIcon, SparklesIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const notificationSchema = z.object({
    title: z.string().min(1, 'Informe o título'),
    body: z.string().min(1, 'Informe o corpo da notificação'),
});

export default function NotificacoesClub() {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const { club } = useClub();

    const hasPackageName = Boolean(club?.packageName && String(club.packageName).trim());

    const form = useForm({
        resolver: zodResolver(notificationSchema),
        defaultValues: {
            title: '',
            body: '',
        },
    });

    const onSubmit = async values => {
        if (!hasPackageName) return;

        try {
            await confirm({
                title: 'Enviar notificação?',
                description: 'Esta notificação será enviada para todos os assinantes do seu clube.',
                confirmationText: 'Enviar',
                cancellationText: 'Cancelar',
            });

            await server.post('club/notifications', {
                title: values.title.trim(),
                body: values.body.trim(),
            });

            form.reset();
            enqueueSnackbar('Notificação enviada com sucesso.');
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;

    if (!hasPackageName) {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold tracking-tight">Notificações</h1>
                    <p className="text-sm text-muted-foreground">
                        Envie notificações push para os assinantes no app do seu clube.
                    </p>
                </div>

                <Card className="relative overflow-hidden border-[#FFB547]/35 bg-[linear-gradient(165deg,#FFF8EB_0%,#FFFFFF_48%,#FFF3D6_100%)] shadow-[0_18px_50px_-28px_rgba(255,181,71,0.65)]">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#F5A623_0%,#FFD280_50%,#F5A623_100%)]"
                    />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-[#FFB547]/20 blur-3xl"
                    />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -bottom-20 -left-10 size-40 rounded-full bg-[#FFD280]/25 blur-3xl"
                    />

                    <CardContent className="relative flex min-h-[340px] flex-col items-center justify-center gap-6 px-6 py-14 text-center">
                        <div className="relative">
                            <div className="absolute inset-0 rounded-full bg-[#FFB547]/35 blur-xl" />
                            <div className="relative flex size-[4.5rem] items-center justify-center rounded-full bg-[linear-gradient(145deg,#FFD280_0%,#FFB547_55%,#F5A623_100%)] text-[#5C3D00] shadow-[0_10px_28px_-8px_rgba(245,166,35,0.7)] ring-4 ring-[#FFE6B0]/80">
                                <SparklesIcon className="size-8" strokeWidth={1.75} />
                            </div>
                        </div>

                        <div className="flex max-w-md flex-col items-center gap-2.5">
                            <span className="rounded-full border border-[#FFB547]/45 bg-[#FFB547]/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A5A00]">
                                Recurso premium
                            </span>
                            <h2 className="text-xl font-semibold tracking-tight text-[#3D2A00]">
                                App personalizado necessário
                            </h2>
                            <p className="text-sm leading-relaxed text-[#6B5428] text-balance">
                                Seu clube ainda não tem aplicativo próprio. As notificações push só funcionam em clubes
                                com app personalizado publicado nas lojas.
                            </p>
                        </div>

                        <p className="text-sm font-semibold text-[#8A5A00]">
                            Fale com seu Gerente de Conta Eurobem
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold tracking-tight">Notificações</h1>
                <p className="text-sm text-muted-foreground">Envie notificações para os membros do seu clube.</p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Nova notificação</CardTitle>
                    <CardDescription>Componha a mensagem que será enviada aos assinantes.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Alert className="mb-4" variant="info">
                        <InfoIcon />
                        <AlertTitle>Atenção</AlertTitle>
                        <AlertDescription>
                            Esta notificação será enviada para todos os assinantes do seu clube.
                        </AlertDescription>
                    </Alert>

                    <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                        <FieldGroup>
                            <Field data-invalid={Boolean(errors.title)}>
                                <FieldLabel htmlFor="title">Título</FieldLabel>
                                <Input
                                    id="title"
                                    aria-invalid={Boolean(errors.title)}
                                    disabled={isSubmitting}
                                    {...form.register('title')}
                                />
                                <FieldError errors={[errors.title]} />
                            </Field>

                            <Field data-invalid={Boolean(errors.body)}>
                                <FieldLabel htmlFor="body">Corpo</FieldLabel>
                                <Textarea
                                    id="body"
                                    rows={6}
                                    aria-invalid={Boolean(errors.body)}
                                    disabled={isSubmitting}
                                    {...form.register('body')}
                                />
                                <FieldError errors={[errors.body]} />
                            </Field>

                            <div className="flex justify-end">
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? <Spinner data-icon="inline-start" /> : null}
                                    Enviar
                                </Button>
                            </div>
                        </FieldGroup>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
